import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Clapperboard, Search, Loader2, Pencil, Trash2, Share2, Lock, Video, FileText, EyeOff, Home as HomeIcon, Sparkles, LayoutTemplate, Newspaper, Archive, ArchiveRestore, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fetchPostsLibrary, deletePost, updatePost, fetchDealerHomePosts, toggleDealerHomePost } from "@/lib/api";
import PostEditorDialog from "@/components/posts/PostEditorDialog";
import VideoShowcaseDialog from "@/components/posts/VideoShowcaseDialog";

// Marketing AI › Posts — every post the user may see (admin: all; dealer: admin's shared + own).
export default function PostsTab({ userRecordId, isAdmin = false, openPost = null }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("all");
  const [editing, setEditing] = useState(openPost || null); // null | {} (new) | post
  useEffect(() => { if (openPost) { setEditing(openPost); setItems((arr) => (arr.some((p) => p.id === openPost.id) ? arr : [openPost, ...arr])); } }, [openPost]);
  const [showcase, setShowcase] = useState(null); // null | {} | post
  const [busyId, setBusyId] = useState("");
  const [homeSlugs, setHomeSlugs] = useState([]); // slugs currently on MY home page tabs

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, h] = await Promise.all([
        fetchPostsLibrary({ userRecordId, includeHidden: true }),
        fetchDealerHomePosts(userRecordId).catch(() => null),
      ]);
      setItems(r?.items || []);
      if (h) setHomeSlugs((h.items || []).filter((x) => !x.is_home).map((x) => String(x.slug || "").toLowerCase()));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load posts");
    } finally {
      setLoading(false);
    }
  }, [userRecordId]);
  useEffect(() => { load(); }, [load]);

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items
      .filter((p) => (scope === "archived" ? p.status === "hidden" : p.status !== "hidden"))
      .filter((p) => (scope === "mine" ? p.owner_rec === userRecordId : scope === "admin" ? p.is_admin_post : scope === "blog" ? p.share_to_network : true))
      .filter((p) => !s || `${p.title} ${p.slug} ${p.caption}`.toLowerCase().includes(s))
      .sort((a, b) => (b.updated_at || b.created_at || "").localeCompare(a.updated_at || a.created_at || ""));
  }, [items, q, scope, userRecordId]);

  function upsert(post) {
    setItems((arr) => (arr.some((p) => p.id === post.id) ? arr.map((p) => (p.id === post.id ? post : p)) : [post, ...arr]));
  }
  const archivedCount = items.filter((p) => p.status === "hidden").length;
  const blogCount = items.filter((p) => p.status !== "hidden" && p.share_to_network).length;
  async function toggle(post, field) {
    setBusyId(post.id);
    try {
      const payload = field === "shared" ? { shared: !post.shared } : field === "home" ? { home: !post.home } : field === "blog" ? { blog: !post.share_to_network } : field === "corner" ? { dealer_corner: !post.dealer_corner } : { status: post.status === "hidden" ? "published" : "hidden" };
      const r = await updatePost(post.id, { user_record_id: userRecordId, ...payload });
      upsert(r.post);
      if (field === "status") toast.success(r.post.status === "hidden" ? `"${post.title}" archived — it's hidden everywhere; find it under Archived to restore.` : `"${post.title}" restored`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally { setBusyId(""); }
  }
  async function toggleMyHome(post) {
    setBusyId(post.id);
    try {
      const r = await toggleDealerHomePost(userRecordId, post.slug);
      setHomeSlugs((r.saved_slugs || []).filter((x) => x !== "__home__"));
      toast.success(r.added ? `"${post.title}" is now the first tab on your home page — reorder it in Profile → Settings → Home page tabs.` : `"${post.title}" removed from your home page tabs`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update home tabs");
    } finally { setBusyId(""); }
  }
  async function remove(post) {
    if (!window.confirm(`Delete "${post.title}"? Dealers who added it to their home tabs will lose that tab.`)) return;
    setBusyId(post.id);
    try {
      await deletePost(post.id, userRecordId);
      setItems((arr) => arr.filter((p) => p.id !== post.id));
      toast.success("Post deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    } finally { setBusyId(""); }
  }

  const chip = (v, label) => (
    <button type="button" onClick={() => setScope(v)} data-testid={`posts-scope-${v}`}
      className={`rounded-full px-3 h-8 text-xs font-semibold ${scope === v ? "bg-zinc-900 text-white" : "bg-white border border-zinc-200 text-zinc-600"}`}>{label}</button>
  );

  if (editing) {
    return (
      <div data-testid="posts-tab">
        <PostEditorDialog userRecordId={userRecordId} isAdmin={isAdmin} post={editing.id ? editing : null}
          onClose={() => setEditing(null)} onSaved={(p) => { upsert(p); setEditing(null); }} />
      </div>
    );
  }
  if (showcase) {
    return (
      <div data-testid="posts-tab">
        <VideoShowcaseDialog userRecordId={userRecordId} isAdmin={isAdmin} post={showcase.id ? showcase : null}
          onClose={() => setShowcase(null)} onSaved={(p) => { upsert(p); setShowcase(null); }} />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="posts-tab">
      <div className="rounded-2xl bg-gradient-to-br from-[#0b2545] to-[#13315c] text-white p-5 sm:p-6 relative overflow-hidden" data-testid="posts-hero">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#c9a34a]/20 blur-2xl" />
        <div className="relative">
          <h2 className="text-base md:text-lg font-bold flex items-center gap-2"><FileText size={18} className="text-[#c9a34a]" /> Your Spa. Your Story. Your Brand. Everywhere!</h2>
          <p className="text-sm font-semibold text-[#c9a34a] mt-1">Create it. Post it. Share it. Spread the Love! One Post. Endless Possibilities!</p>
          <p className="text-sm text-white/80 mt-2 max-w-2xl">Introducing <b className="text-white">OreVital Post Studio™</b> — your personal content creation and marketing powerhouse! Create engaging posts, upload them to your blog, feature them on your homepage, and share across Instagram, Facebook, SMS, and email — all in just a few clicks!</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search posts…" className="pl-9 h-9 rounded-xl" data-testid="posts-search" />
        </div>
        {chip("all", "All")}{chip("mine", "Mine")}{isAdmin ? null : chip("admin", "OreVital")}
        {isAdmin && (
          <button type="button" onClick={() => setScope("blog")} data-testid="posts-scope-blog"
            className={`rounded-full px-3 h-8 text-xs font-semibold inline-flex items-center gap-1 ${scope === "blog" ? "bg-zinc-900 text-white" : "bg-white border border-zinc-200 text-zinc-600"}`}>
            <Newspaper size={12} /> Blog{blogCount ? ` (${blogCount})` : ""}
          </button>
        )}
        <button type="button" onClick={() => setScope("archived")} data-testid="posts-scope-archived"
          className={`rounded-full px-3 h-8 text-xs font-semibold inline-flex items-center gap-1 ${scope === "archived" ? "bg-zinc-900 text-white" : "bg-white border border-zinc-200 text-zinc-600"}`}>
          <Archive size={12} /> Archived{archivedCount ? ` (${archivedCount})` : ""}
        </button>
        <Button onClick={() => setShowcase({})} variant="outline" className="h-9 rounded-xl text-xs font-semibold" data-testid="posts-new-showcase">
          <Clapperboard size={14} className="mr-1.5" /> Video showcase
        </Button>
        <Button onClick={() => setEditing({})} className="h-9 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold" data-testid="posts-new">
          <Plus size={14} className="mr-1.5" /> New post
        </Button>
      </div>
      <p className="text-xs text-zinc-500">
        Posts become tabs on home pages (Profile → Settings → Home page tabs). {isAdmin ? "Your posts are visible to every dealer; tick Home to make one a default tab." : "Your posts are private until you share them with other dealers."}
      </p>

      {loading ? (
        <div className="py-10 text-center text-sm text-zinc-500"><Loader2 className="inline animate-spin mr-2" size={16} />Loading posts…</div>
      ) : list.length === 0 ? (
        <div className="py-10 text-center text-sm text-zinc-500 rounded-2xl border border-dashed border-zinc-200 bg-white" data-testid="posts-empty">{scope === "archived" ? "Nothing archived." : scope === "blog" ? "Nothing on the blog yet — use “Push to blog” on a post." : "No posts yet — create one or build a video showcase."}</div>
      ) : (
        <ul className="grid gap-2 sm:grid-cols-2" data-testid="posts-list">
          {list.map((p) => (
            <li key={p.id} className="rounded-2xl border border-zinc-200 bg-white p-3 flex gap-3" data-testid={`post-card-${p.slug}`}>
              <div className="h-16 w-16 shrink-0 rounded-xl bg-zinc-100 overflow-hidden flex items-center justify-center text-zinc-400">
                {p.thumbnail_url || p.image_url ? <img src={p.thumbnail_url || p.image_url} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} data-testid={`post-thumb-${p.slug}`} /> : p.video_ids?.length ? <Clapperboard size={20} /> : p.video_url ? <Video size={20} /> : <FileText size={20} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-zinc-900 truncate" data-testid={`post-title-${p.slug}`}>{p.title}</div>
                    <div className="text-[11px] text-zinc-400 font-mono truncate flex items-center gap-1" data-testid={`post-url-${p.slug}`}>
                      <a href={p.url || `https://orevital.com/post/${p.slug}`} target="_blank" rel="noreferrer" className="truncate hover:text-violet-700 hover:underline" title="Open the public post page">{(p.url || `https://orevital.com/post/${p.slug}`).replace(/^https?:\/\//, "")}</a>
                      <button type="button" onClick={() => { navigator.clipboard?.writeText(p.url || `https://orevital.com/post/${p.slug}`); toast.success("Post link copied"); }} className="shrink-0 text-zinc-400 hover:text-zinc-700" title="Copy link" data-testid={`post-copy-url-${p.slug}`}><Copy size={11} /></button>
                    </div>
                  </div>
                  {p.can_edit && (
                    <div className="flex items-center gap-1 shrink-0">
                      {p.video_ids?.length ? (
                        <button type="button" onClick={() => setShowcase(p)} className="h-7 w-7 rounded-lg hover:bg-amber-50 flex items-center justify-center text-amber-700" title="Change videos" data-testid={`post-videos-${p.slug}`}><Clapperboard size={13} /></button>
                      ) : null}
                      <button type="button" onClick={() => setEditing(p)} className="h-7 w-7 rounded-lg hover:bg-zinc-100 flex items-center justify-center text-zinc-600" title="Edit post (header, text, AI)" data-testid={`post-edit-${p.slug}`}><Pencil size={13} /></button>
                      <button type="button" onClick={() => remove(p)} disabled={busyId === p.id} className="h-7 w-7 rounded-lg hover:bg-rose-50 flex items-center justify-center text-rose-500" title="Delete" data-testid={`post-delete-${p.slug}`}><Trash2 size={13} /></button>
                    </div>
                  )}
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[10px] font-semibold">
                  {p.is_admin_post && <span className="rounded-full bg-sky-50 text-sky-700 px-2 py-0.5">OreVital</span>}
                  {p.video_ids?.length ? <span className="rounded-full bg-amber-50 text-amber-700 px-2 py-0.5">{p.video_ids.length} video{p.video_ids.length === 1 ? "" : "s"}</span> : null}
                  {p.video_url && <span className="rounded-full bg-zinc-100 text-zinc-600 px-2 py-0.5 inline-flex items-center gap-1"><Video size={10} /> video on top</span>}
                  {p.status === "hidden" && <span className="rounded-full bg-zinc-800 text-white px-2 py-0.5 inline-flex items-center gap-1"><EyeOff size={10} /> hidden</span>}
                  {p.can_edit ? (
                    <>
                      {!p.is_admin_post && (
                        <button type="button" onClick={() => toggle(p, "shared")} disabled={busyId === p.id} data-testid={`post-share-${p.slug}`}
                          className={`rounded-full px-2 py-0.5 inline-flex items-center gap-1 ${p.shared ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
                          {p.shared ? <Share2 size={10} /> : <Lock size={10} />} {p.shared ? "Shared with dealers" : "Private"}
                        </button>
                      )}
                      {isAdmin && (
                        <button type="button" onClick={() => toggle(p, "home")} disabled={busyId === p.id} data-testid={`post-home-${p.slug}`}
                          className={`rounded-full px-2 py-0.5 inline-flex items-center gap-1 ${p.home ? "bg-violet-100 text-violet-700" : "bg-zinc-100 text-zinc-600"}`}>
                          <HomeIcon size={10} /> {p.home ? "Default home tab" : "Not default"}
                        </button>
                      )}
                      <button type="button" onClick={() => toggle(p, "corner")} disabled={busyId === p.id} data-testid={`post-corner-${p.slug}`}
                        title="Show this post in the Dealer Corner slider on the front page"
                        className={`rounded-full px-2 py-0.5 inline-flex items-center gap-1 ${p.dealer_corner ? "bg-teal-100 text-teal-800" : "bg-zinc-100 text-zinc-600"}`}>
                        <Sparkles size={10} /> {p.dealer_corner ? "In Dealer Corner" : "Add to Dealer Corner"}
                      </button>
                      {isAdmin && (
                        <button type="button" onClick={() => toggle(p, "blog")} disabled={busyId === p.id} data-testid={`post-blog-${p.slug}`}
                          title="Publish this post on the public OreVital blog (orevital.com/blog)"
                          className={`rounded-full px-2 py-0.5 inline-flex items-center gap-1 ${p.share_to_network ? "bg-rose-100 text-rose-800" : "bg-zinc-100 text-zinc-600"}`}>
                          <Newspaper size={10} /> {p.share_to_network ? "On the blog" : "Push to blog"}
                        </button>
                      )}
                      <button type="button" onClick={() => toggle(p, "status")} disabled={busyId === p.id} data-testid={`post-hide-${p.slug}`}
                        title={p.status === "hidden" ? "Restore — show it again everywhere" : "Archive — hides it from the blog, home tabs, Dealer Corner and this list (Posts.status = hidden in Airtable)"}
                        className={`rounded-full px-2 py-0.5 inline-flex items-center gap-1 ${p.status === "hidden" ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
                        {p.status === "hidden" ? <ArchiveRestore size={10} /> : <Archive size={10} />} {p.status === "hidden" ? "Restore" : "Archive"}
                      </button>
                    </>
                  ) : (
                    <span className="rounded-full bg-zinc-100 text-zinc-500 px-2 py-0.5">read-only</span>
                  )}
                </div>
                {(() => { const on = homeSlugs.includes(String(p.slug || "").toLowerCase()); return (
                  <button type="button" onClick={() => toggleMyHome(p)} disabled={busyId === p.id} data-testid={`post-my-home-${p.slug}`}
                    title={on ? "Remove this tab from your home page" : "Add as the first tab on your home page (reorder later in Profile → Settings → Home page tabs)"}
                    className={`mt-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold inline-flex items-center gap-1 ${on ? "bg-indigo-600 text-white" : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"}`}>
                    <LayoutTemplate size={10} /> {on ? "On my home page" : "Push to my home page"}
                  </button>
                ); })()}
              </div>
            </li>
          ))}
        </ul>
      )}

    </div>
  );
}
